import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Square, Sparkles, ChevronRight, ChevronDown, History, Trash2, Send, Lock, Bug } from 'lucide-react'
import { aiBuilderApi, agentRunApi, toolsApi } from '../../../lib/apiClient'
import { toast } from '../../../lib/toast'
import { useToolsStore } from '../../../store/toolsStore'
import Button from '../../../ui/Button/Button'
import { AiBuilderConversationSummary } from '../../../widget/types'
import styles from './AiChatPanel.module.css'
import { ActivityEvent, AgentState, AiChatPanelProps, DebugEntry, DuplicateChoice, Message } from './aiChatTypes'
import { planDuplicateAction, cloneTitle } from './duplicateActions'
import { formatDate } from './aiChatFunctions'
import { CONNECTOR_TYPE_DESCRIPTIONS } from './aiChatConstants'
import { useSettingsStore } from '../../../store/settingsStore'
import {
  selectSkillsForSession,
  triggerFrontLoadedExtraction,
  triggerSuccessExtraction,
  triggerBlockedErrorExtraction,
} from './aiSkillsIntegration'
import { MessageBubble } from './messageBubble/MessageBubble'
import { getTypeMeta } from '../../../utils/utils'
import { TagsEnv } from '../../../ui/Tags/TagsEnv'


export default function AiChatPanel({
  wizardUpdateRef, toolId, toolTitle, isNew, getCurrentValues, connectors,
  linkConversationRef,
}: AiChatPanelProps) {
  const navigate = useNavigate()
  const addTool = useToolsStore(s => s.addTool)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [conversations, setConversations] = useState<AiBuilderConversationSummary[]>([])
  const [historyOpen, setHistoryOpen] = useState(false)
  const [conversationTitle, setConversationTitle] = useState('')
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | null>(
    () => (getCurrentValues?.()?.mainConnectionId as string) ?? null
  )

  // Agent loop state
  const [agentState, setAgentState] = useState<AgentState>('idle')
  const [streamingText, setStreamingText] = useState<string>('')
  const [thinkingExpanded, setThinkingExpanded] = useState(false)
  const activeRunIdRef = useRef<string | null>(null)

  // Debug mode
  const isDebugEnabled = useSettingsStore(s => s.isAiBuilderDebugEnabled())
  const [debugExpanded, setDebugExpanded] = useState(false)
  const [debugLog, setDebugLog] = useState<Record<string, DebugEntry>>({})
  const debugLogRef = useRef<Record<string, DebugEntry>>({})
  const sseRef = useRef<EventSource | null>(null)
  const messagesAccRef = useRef<Message[]>([])
  const lastTestResponseRef = useRef<string>('')

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const streamingBoxRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const historyDropdownRef = useRef<HTMLDivElement>(null)
  const suppressResetRef = useRef(false)
  const messagesRef = useRef<Message[]>([])

  useEffect(() => {
    if (suppressResetRef.current) {
      suppressResetRef.current = false
      aiBuilderApi.listConversations(toolId)
        .then(list => setConversations(list))
        .catch(() => {})
      return
    }
    setMessages([])
    setConversationId(null)
    setConversationTitle('')
    setAgentState('idle')
    aiBuilderApi.listConversations(toolId)
      .then(list => setConversations(list))
      .catch(() => {})
  }, [toolId])

  useEffect(() => {
    if (!historyOpen) return
    const handler = (e: MouseEvent) => {
      if (historyDropdownRef.current && !historyDropdownRef.current.contains(e.target as Node)) {
        setHistoryOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [historyOpen])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading, agentState])

  useEffect(() => {
    if (streamingBoxRef.current) {
      streamingBoxRef.current.scrollTop = streamingBoxRef.current.scrollHeight
    }
  }, [streamingText])

  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (messages.length === 0) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [messages.length])

  // Leaving the panel with a live stream used to leave the EventSource open, which keeps
  // the server-side agent loop (and its token spend) running for a run nobody is watching.
  useEffect(() => () => {
    sseRef.current?.close()
    sseRef.current = null
  }, [])

  useEffect(() => {
    if (!linkConversationRef) return
    linkConversationRef.current = async (newToolId: number) => {
      if (!conversationId) return
      suppressResetRef.current = true
      try {
        await aiBuilderApi.updateConversation(conversationId, {
          messages: messagesRef.current,
          toolId: newToolId,
        })
      } catch {
        // silent - tool creation proceeds regardless
      }
    }
  }, [conversationId, linkConversationRef])

  const autoResizeTextarea = () => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = Math.min(ta.scrollHeight, 160) + 'px'
    ta.style.overflowY = ta.scrollHeight > 160 ? 'auto' : 'hidden'
  }

  const autoSave = useCallback(async (nextMessages: Message[], currentConvId: string | null) => {
    const payload = { messages: nextMessages, toolId }
    try {
      if (currentConvId === null) {
        const conversation = await aiBuilderApi.createConversation(payload)
        setConversationId(conversation.id)
        setConversationTitle(conversation.title)
        setConversations(prev => [{ id: conversation.id, title: conversation.title, updatedAt: conversation.updatedAt }, ...prev])
        return conversation.id
      } else {
        await aiBuilderApi.updateConversation(currentConvId, payload)
        setConversations(prev =>
          prev.map(c => c.id === currentConvId ? { ...c, updatedAt: new Date().toISOString() } : c)
        )
        return currentConvId
      }
    } catch {
      return currentConvId
    }
  }, [toolId])

  const handleNewChat = useCallback(() => {
    setMessages([])
    setConversationId(null)
    setConversationTitle('')
    setHistoryOpen(false)
    setAgentState('idle')
  }, [])

  const handleLoadConversation = useCallback(async (id: string) => {
    try {
      const conversation = await aiBuilderApi.getConversation(id)
      setMessages(conversation.messages ?? [])
      setConversationId(conversation.id)
      setConversationTitle(conversation.title)
      setHistoryOpen(false)
    } catch {
      toast.error('Failed to load conversation')
    }
  }, [])

  const handleDeleteConversation = useCallback(async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await aiBuilderApi.deleteConversation(id)
      setConversations(prev => prev.filter(c => c.id !== id))
      if (conversationId === id) handleNewChat()
    } catch {
      toast.error('Failed to delete conversation')
    }
  }, [conversationId, handleNewChat])

  const handleUndo = useCallback((msgId: string, previousValues: Record<string, unknown>) => {
    wizardUpdateRef.current?.(previousValues)
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, undone: true } : m))
  }, [wizardUpdateRef])

  const handleApprovalResponse = useCallback((approved: boolean) => {
    if (activeRunIdRef.current) {
      setMessages(prev => prev.map(m => m.awaitingApproval ? { ...m, awaitingApproval: false, approvalGranted: approved } : m))
      agentRunApi.approve(activeRunIdRef.current, approved).catch(() => {})
      if (agentState === 'awaiting-approval') setAgentState('generating')
    }
  }, [agentState])

  const handleDuplicateResponse = useCallback(async (choice: DuplicateChoice) => {
    const runId = activeRunIdRef.current
    const tool = messagesRef.current.find(m => m.awaitingDuplicate)?.duplicateTool
   messagesAccRef.current = messagesAccRef.current.map(m => m.awaitingDuplicate
      ? { ...m, awaitingDuplicate: false, duplicateResolution: choice }
      : m)
    setMessages([...messagesAccRef.current])
    if (agentState === 'awaiting-approval') setAgentState('generating')

    const plan = planDuplicateAction(choice, tool)
    if (runId) agentRunApi.approve(runId, plan.approve).catch(() => {})

    if (plan.navigateTo) {
      navigate(plan.navigateTo)
      return
    }
    if (plan.cloneOf) {
      try {
        const res = await toolsApi.duplicate(plan.cloneOf.id, cloneTitle(plan.cloneOf.title, plan.cloneOf.name), plan.cloneOf.name)
        addTool(res.tool)
        navigate(`/toolslist/${res.tool.id}?ai=true`)
      } catch (e: any) {
        toast.error(`Failed to duplicate tool: ${e?.message ?? 'unknown error'}`)
      }
    }
  }, [agentState, navigate, addTool])

  const handleConnectorChange = useCallback((newId: string | null) => {
    setSelectedConnectorId(newId)
    wizardUpdateRef.current?.({ mainConnectionId: newId })
  }, [wizardUpdateRef])

  // ─── SSE agent run ─────────────────────────────────────────────────────────

  const startAgentRun = useCallback(async (
    nextMessages: Message[],
    convId: string | null,
    skillsBlock: string,
    connectorTypeId?: number,
  ) => {
    if (activeRunIdRef.current) {
      agentRunApi.abort(activeRunIdRef.current).catch(() => {})
      sseRef.current?.close()
      sseRef.current = null
      activeRunIdRef.current = null
    }

    messagesAccRef.current = [...nextMessages]
    debugLogRef.current = {}
    setDebugLog({})
    let currentConvId = convId

    let runId: string
    let ticket: string
    try {
      const res = await agentRunApi.startRun({
        mode: isNew ? 'new' : 'edit',
        toolTitle,
        currentValues: getCurrentValues?.() ?? {},
        selectedConnectorId: selectedConnectorId ?? undefined,
        connectorTypeId,
        messages: nextMessages.map(m => ({
          role: m.role as 'user' | 'assistant',
          content: m.undone ? `${m.content} (the user undid this change)` : m.content,
        })),
        skillsBlock,
        toolId,
        lastTestResponse: lastTestResponseRef.current || undefined,
      })
      runId = res.runId
      ticket = res.ticket
    } catch {
      setLoading(false)
      return
    }

    activeRunIdRef.current = runId
    setAgentState('generating')

    const addActivity = (event: ActivityEvent) => {
      const msg: Message = {
        id: `act-${Date.now()}-${Math.random()}`,
        role: 'assistant',
        content: event.text,
        activityEvent: event,
      }
      messagesAccRef.current = [...messagesAccRef.current, msg]
      setMessages([...messagesAccRef.current])
    }

    // Snapshot the prior values of the keys a patch will change, so they can be undone.
    const snapshotPrev = (patch: Record<string, unknown>) => {
      const before = getCurrentValues?.() ?? {}
      const prev: Record<string, unknown> = {}
      for (const key of Object.keys(patch)) prev[key] = before[key]
      return prev
    }

    // Apply a patch and record it as an undoable field-update message.
    const addFieldUpdate = (text: string, patch: Record<string, unknown>) => {
      const previousValues = snapshotPrev(patch)
      wizardUpdateRef.current?.(patch)
      messagesAccRef.current = [...messagesAccRef.current, {
        id: `patch-${Date.now()}-${Math.random()}`,
        role: 'assistant',
        content: text,
        isFieldUpdate: true,
        fieldPatch: patch,
        previousValues,
      }]
      setMessages([...messagesAccRef.current])
    }

    const es = agentRunApi.streamEvents(runId, ticket)
    sseRef.current = es

    return new Promise<void>((resolve) => {
      // Ends a run whose stream is gone, and says so. A dropped SSE connection used to
      // reset the panel to idle in silence, so the user was left with an activity line,
      // no reply, and no way to tell the run had died rather than still being at work.
      const failRun = (text: string) => {
        if (activeRunIdRef.current !== runId) { resolve(); return }
        activeRunIdRef.current = null
        es.close()
        sseRef.current = null
        setStreamingText('')
        setAgentState('idle')
        setLoading(false)
        messagesAccRef.current = [...messagesAccRef.current, {
          id: `sse-lost-${Date.now()}`,
          role: 'assistant',
          content: text,
        }]
        setMessages([...messagesAccRef.current])
        resolve()
      }

      const finish = async () => {
        setStreamingText('')
        setLoading(false)
        es.close()
        sseRef.current = null
        activeRunIdRef.current = null
        const savedId = await autoSave(messagesAccRef.current, currentConvId) as string | null
        setConversationId(savedId)
        resolve()
      }

      es.onmessage = async (e: MessageEvent<string>) => {
        // Unguarded, one malformed frame threw out of the handler - and if the frame it
        // landed on was the terminal one, finish() never ran and the panel stayed busy.
        let event: ActivityEvent | null = null
        try {
          event = JSON.parse(e.data) as ActivityEvent
        } catch {
          return
        }
        if (!event) return

        if (event.type === 'debug-prompt') {
          let systemPrompt = ''
          let messages: Array<{ role: string; content: string }> = []
          try {
            const parsed = JSON.parse(event.detail ?? '{}')
            systemPrompt = parsed.systemPrompt ?? ''
            messages = parsed.messages ?? []
          } catch { /* use empty defaults */ }
          const entry: DebugEntry = { systemPrompt, messages, thinking: '', response: '' }
          debugLogRef.current = { ...debugLogRef.current, [event.text]: entry }
          setDebugLog({ ...debugLogRef.current })
          return
        }

        if (event.type === 'streaming-thinking') {
          if (event.debugKey && event.text && debugLogRef.current[event.debugKey] !== undefined) {
            debugLogRef.current = {
              ...debugLogRef.current,
              [event.debugKey]: { ...debugLogRef.current[event.debugKey], thinking: event.text },
            }
            setDebugLog({ ...debugLogRef.current })
          }
          return
        }

        if (event.type === 'streaming-token') {
          setStreamingText(event.text)
          if (event.debugKey && event.text && debugLogRef.current[event.debugKey] !== undefined) {
            debugLogRef.current = {
              ...debugLogRef.current,
              [event.debugKey]: { ...debugLogRef.current[event.debugKey], response: event.text },
            }
            setDebugLog({ ...debugLogRef.current })
          }
          return
        }

        if (event.type === 'ai-patched') {
          if (event.patch) addFieldUpdate(event.text, event.patch)
          else addActivity(event)
          return
        }

        if (event.type === 'ai-created') {
          if (event.tool) addFieldUpdate(event.text, event.tool)
          else addActivity(event)
          return
        }

        if (event.type === 'ai-reply') {
          messagesAccRef.current = [...messagesAccRef.current, {
            id: `reply-${Date.now()}-${Math.random()}`,
            role: 'assistant',
            content: event.text,
          }]
          setMessages([...messagesAccRef.current])
          setAgentState('success')
          triggerSuccessExtraction(messagesAccRef.current.map(m => ({ role: m.role, content: m.content })), connectorTypeId)
          await finish()
          return
        }

        if (event.type === 'awaiting-approval') {
          setAgentState('awaiting-approval')
          messagesAccRef.current = [...messagesAccRef.current, {
            id: `approval-${Date.now()}`, role: 'assistant', content: event.text, awaitingApproval: true,
          }]
          setMessages([...messagesAccRef.current])
          return
        }

        if (event.type === 'duplicate-detected') {
          setAgentState('awaiting-approval')
          messagesAccRef.current = [...messagesAccRef.current, {
            id: `dup-${Date.now()}`,
            role: 'assistant',
            content: event.text,
            awaitingDuplicate: true,
            duplicateTool: event.tool as { id: number; name: string; title?: string } | undefined,
          }]
          setMessages([...messagesAccRef.current])
          return
        }

        if (event.type === 'loop-success') {
          addActivity(event)
          setAgentState('success')
          triggerSuccessExtraction(messagesAccRef.current.map(m => ({ role: m.role, content: m.content })), connectorTypeId)
          await finish()
          return
        }

        if (event.type === 'loop-saved') {
          addActivity(event)
          setAgentState('idle')
          await finish()
          return
        }

        if (event.type === 'loop-clarification') {
          messagesAccRef.current = [...messagesAccRef.current, {
            id: `clarify-${Date.now()}-${Math.random()}`,
            role: 'assistant',
            content: event.text,
          }]
          setMessages([...messagesAccRef.current])
          setAgentState('blocked')
          triggerBlockedErrorExtraction(messagesAccRef.current.map(m => ({ role: m.role, content: m.content })), event.text, connectorTypeId)
          await finish()
          return
        }

        if (event.type === 'loop-blocked') {
          addActivity(event)
          setAgentState('blocked')
          triggerBlockedErrorExtraction(messagesAccRef.current.map(m => ({ role: m.role, content: m.content })), event.text, connectorTypeId)
          await finish()
          return
        }

        if (event.type === 'patching') {
          setAgentState('repairing')
          if (event.patch) addFieldUpdate(event.text, event.patch)
          else if (event.tool) addFieldUpdate(event.text, event.tool)
          else addActivity(event)
          return
        }

        if (event.type === 'test-success') {
          // Stash the real response body so the next run can ground filter/discovery answers.
          lastTestResponseRef.current = (event.detail ?? '').replace(/^Response:\s*/, '').trim()
          addActivity(event)
          return
        }

        if (event.type === 'generating') setAgentState('generating')
        else if (event.type === 'validating') setAgentState('validating')
        else if (event.type === 'connecting' || event.type === 'executing') setAgentState('testing' as AgentState)
        else if (event.type === 'analyzing') setAgentState('repairing')

        addActivity(event)
      }

      // Also fires on the normal end of a run, once the server completes the stream.
      // failRun() checks activeRunIdRef for exactly that: a finished run has already
      // cleared it, so only a genuine mid-run drop reports an error.
      es.onerror = async () => {
        console.debug(`[agent-run ${runId}] ${new Date().toISOString()} stream error, readyState ${es.readyState}, run still active: ${activeRunIdRef.current === runId}`)
        failRun('The connection to the server was lost before the run finished. Please send your message again.')
      }
    })
  }, [autoSave, getCurrentValues, isNew, selectedConnectorId, toolId, toolTitle, wizardUpdateRef])

  // ─── Send / Stop ───────────────────────────────────────────────────────────

  const handleSend = useCallback(async (text?: string) => {
    const messageText = (text ?? input).trim()
    if (!messageText || loading) return

    const userMessage: Message = { id: Date.now().toString(), role: 'user', content: messageText }
    const nextMessages = [...messages, userMessage]
    setMessages(nextMessages)
    setInput('')
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.overflowY = 'hidden'
    }
    setLoading(true)

    const connectorTypeId = connectors?.find(c => c.id === selectedConnectorId)?.connectionTypeId
    const skillsBlock = await selectSkillsForSession(messageText, connectorTypeId)
    triggerFrontLoadedExtraction(
      nextMessages.map(m => ({ role: m.role, content: m.content })),
      connectorTypeId,
    )

    await startAgentRun(nextMessages, conversationId, skillsBlock, connectorTypeId)
    setLoading(false)
  }, [input, messages, loading, startAgentRun, conversationId, connectors, selectedConnectorId])

  const handleStop = useCallback(() => {
    if (activeRunIdRef.current) {
      agentRunApi.abort(activeRunIdRef.current).catch(() => {})
      sseRef.current?.close()
      sseRef.current = null
      activeRunIdRef.current = null
    }
    setStreamingText('')
    setLoading(false)
    if (agentState !== 'idle') setAgentState('idle')
    setMessages(prev => [
      ...prev.filter(m => !m.awaitingApproval && !m.awaitingDuplicate),
      { id: (Date.now() + 1).toString(), role: 'assistant', content: 'Request cancelled.' },
    ])
  }, [agentState])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const currentTitle = conversationTitle
    || (messages.length === 0 ? 'New conversation' : messages.find(m => m.role === 'user')?.content.slice(0, 50) ?? 'New conversation')

  const isRunning = loading || (agentState !== 'idle' && agentState !== 'success' && agentState !== 'blocked')


  return (
    <div className={styles.panel}>
      {/* Top bar: history + debug toggle */}
      <div className={styles.topBar}>
        <div className={styles.historyWrapper} ref={historyDropdownRef}>
          <button
            className={styles.historyBtn}
            onClick={() => setHistoryOpen(o => !o)}
            title="Conversation history"
          >
            <History size={14} />
            <span className={styles.historyBtnTitle}>{currentTitle}</span>
            <ChevronRight size={12} className={`${styles.historyChevron} ${historyOpen ? styles.historyChevronOpen : ''}`} />
          </button>

          {historyOpen && (
            <div className={styles.historyDropdown}>
              {conversations.length === 0 ? (
                <div className={styles.historyEmpty}>No saved conversations</div>
              ) : (
                conversations.map(conversation => (
                  <div
                    key={conversation.id}
                    className={`${styles.historyRow} ${conversation.id === conversationId ? styles.historyRowActive : ''}`}
                    onClick={() => handleLoadConversation(conversation.id)}
                  >
                    <span className={styles.historyRowTitle}>{conversation.title}</span>
                    <span className={styles.historyRowDate}>{formatDate(conversation.updatedAt)}</span>
                    <button
                      className={styles.historyDeleteBtn}
                      onClick={(e) => handleDeleteConversation(conversation.id, e)}
                      title="Delete conversation"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {isDebugEnabled && (
          <button
            className={`${styles.debugToggleBtn} ${debugExpanded ? styles.debugToggleBtnActive : ''}`}
            onClick={() => setDebugExpanded(v => !v)}
            title={debugExpanded ? 'Hide debug info' : 'Show debug info'}
          >
            <Bug size={13} />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className={styles.messages}>
        {messages.length === 0 && !loading && isNew && (
          <div className={styles.emptyNew}>
            {/* Bot greeting */}
            <div className={styles.greetingRow}>
              <div className={styles.greetingAvatar}>
                {/* <Bot size={20} /> */}
                <Sparkles size={20} color="#f9e77e"/>
              </div>
              <div className={styles.greetingBubble}>
                Hi! I'll help you build a new tool.
                <br /><br />
                Before we begin, choose the connector you'd like to use.
              </div>
            </div>

            {/* Connector selection */}
            <div className={styles.connectorSection}>
              <p className={styles.connectorSectionTitle}>Select a connector</p>
              <p className={styles.connectorSectionSub}>
                This helps me generate a more accurate and relevant tool.{' '}
              </p>

              <div className={styles.connectorCards}>
                {(connectors ?? []).length === 0 ? (
                  <p className={styles.connectorEmpty}>No connectors available</p>
                ) : (
                  (connectors ?? []).map(c => (
                    <div
                      key={c.id}
                      className={`${styles.connectorCard} ${selectedConnectorId === c.id ? styles.connectorCardSelected : ''}`}
                      onClick={() => handleConnectorChange(c.id)}
                    >
                      <TagsEnv env={c.environment as 'test' | 'dev' | 'prod' | undefined} isToolDisabledByConnector={!c || !c.environment} />
                      <img src={getTypeMeta(c.connectionTypeId)?.icon} alt={c.connectionName} className={styles.connectorsIcons}/>
                      <div className={styles.connectorCardBody}>
                        <span className={styles.connectorCardName}>{c.connectionName}</span>
                        <span className={styles.connectorCardDesc}>
                          {CONNECTOR_TYPE_DESCRIPTIONS[c.connectionTypeId] ?? 'Connect and query data'}
                        </span>
                      </div>
                      <div className={`${styles.radioOuter} ${selectedConnectorId === c.id ? styles.radioOuterSelected : ''}`}>
                        {selectedConnectorId === c.id && <div className={styles.radioInner} />}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className={styles.connectorTip}>
                <Sparkles size={13} className={styles.connectorTipIcon} color="#121034"/>
                <span>Once you select a connector, I'll guide you through describing the tool and building it step by step.</span>
              </div>
            </div>

            <hr className={styles.sectionDivider} />

            {/* What's next */}
            <div className={styles.whatsNext}>
              <div className={styles.whatsNextLeft}>
                <div className={styles.whatsNextText}>
                  <span className={styles.whatsNextTitle}>What's next</span>
                  <span className={styles.whatsNextDesc}>
                    Describe what you want the tool to do.<br />I'll take care of the rest.
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {messages.length === 0 && !loading && !isNew && (
          <div className={styles.emptyChat}>
            <Sparkles size={28} className={styles.emptyChatIcon} color="#f9e77e" />
            <p className={styles.emptyChatTitle}>Ask AI to update this tool</p>
            <p className={styles.emptyChatHint}>Describe a change and AI will update the form fields.</p>
          </div>
        )}

        {messages.map(msg => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            onUndo={handleUndo}
            onApprovalResponse={handleApprovalResponse}
            onDuplicateResponse={handleDuplicateResponse}
            debugLog={debugLog}
            debugExpanded={debugExpanded}
          />
        ))}

        {loading && agentState !== 'idle' && agentState !== 'awaiting-approval' && (
          <div className={styles.inProgressLine}>
            <span className={styles.spinningDot} />
            <div className={styles.inProgressBody}>
              <div className={styles.inProgressText}>
                {agentState === 'generating' ? (
                  <button className={styles.thinkingToggle} onClick={() => setThinkingExpanded(v => !v)}>
                    Thinking…
                    <ChevronDown size={12} className={`${styles.thinkingChevron} ${thinkingExpanded ? styles.thinkingChevronOpen : ''}`} />
                  </button>
                ) : agentState === 'validating' ? 'Validating config…'
                  : agentState === 'testing' ? 'Running test…'
                  : agentState === 'repairing' ? 'Analyzing error…'
                  : 'Working…'}
              </div>
              {agentState === 'generating' && thinkingExpanded && (
                streamingText
                  ? (
                    <div className={styles.streamingBox} ref={streamingBoxRef}>
                      {streamingText}<span className={styles.streamingCursor} />
                    </div>
                  )
                  : (
                    <div className={styles.thinkingBox}>
                      <span className={styles.thinkingPhrase} />
                    </div>
                  )
              )}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={styles.inputRow}>
        <div className={styles.inputShell}>
          <span className={styles.inputSparkle}>
            <Sparkles size={14} />
          </span>
          <textarea
            ref={textareaRef}
            className={styles.textarea}
            placeholder="Let's start build a tool together…"
            value={input}
            onChange={e => { setInput(e.target.value); autoResizeTextarea() }}
            onKeyDown={handleKeyDown}
            disabled={isRunning}
            rows={1}
          />
          {isRunning ? (
            <Button variant="primary" size="md" onClick={handleStop} className={styles.sendButton}>
              <Square size={15} />
            </Button>
          ) : (
            <Button
              variant="primary"
              size="md"
              onClick={() => handleSend()}
              disabled={!input.trim() || (isNew && !selectedConnectorId)}
              className={styles.sendButton}
            >
              <Send size={15} />
            </Button>
          )}
        </div>
        {isNew && !selectedConnectorId && (
          <div className={styles.inputLockHint}>
            <Lock size={11} />
            <span>Select a connector to continue</span>
          </div>
        )}
      </div>
    </div>
  )
}
